import { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/Layout';

const loadHomepage = () => import('./pages/Homepage');
const loadWhatIsAdaptiveGolf = () => import('./pages/WhatIsAdaptiveGolf');
const loadTryGolf = () => import('./pages/TryGolf');
const loadCompetition = () => import('./pages/Competition');
const loadAgaCalendar = () => import('./pages/AgaCalendar');
const loadMedia = () => import('./pages/Media');
const loadPlayerStories = () => import('./pages/PlayerStories');
const loadPlayerStoryDetail = () => import('./pages/PlayerStoryDetail');
const loadMediaDiscoveryProgrammeJan26 = () => import('./pages/MediaDiscoveryProgrammeJan26');
const loadMediaDiscoveryDayVisualImpairment = () => import('./pages/MediaDiscoveryDayVisualImpairment');
const loadMediaAdaptiveGolfTournament = () => import('./pages/MediaAdaptiveGolfTournament');
const loadGetInvolved = () => import('./pages/GetInvolved');
const loadDonate = () => import('./pages/Donate');
const loadNews = () => import('./pages/News');
const loadNewsPostMilestone = () => import('./pages/NewsPostMilestone');
const loadNewsPostDiscoveryDay = () => import('./pages/NewsPostDiscoveryDay');
const loadNewsAdaptiveOpen = () => import('./pages/NewsAdaptiveOpen');
const loadNewsCzechDisableOpen = () => import('./pages/NewsCzechDisableOpen');
const loadConnect = () => import('./pages/Connect');
const loadBlog = () => import('./pages/Blog');
const loadBlogPostEmpowerment = () => import('./pages/BlogPostEmpowerment');
const loadBlogPostVolunteers = () => import('./pages/BlogPostVolunteers');
const loadBlogPostAdaptiveCoaching = () => import('./pages/BlogPostAdaptiveCoaching');
const loadBlogPostBeyondVision = () => import('./pages/BlogPostBeyondVision');
const loadBlogPostCzech = () => import('./pages/BlogPostCzech');
const loadBlogPostCol = () => import('./pages/BlogPostCol');
const loadBlogPostCol2 = () => import('./pages/BlogPostCol2');
const loadAboutUs = () => import('./pages/AboutUs');
const loadTeam = () => import('./pages/Team');

const Homepage = lazy(loadHomepage);
const WhatIsAdaptiveGolf = lazy(loadWhatIsAdaptiveGolf);
const TryGolf = lazy(loadTryGolf);
const Competition = lazy(loadCompetition);
const AgaCalendar = lazy(loadAgaCalendar);
const Media = lazy(loadMedia);
const PlayerStories = lazy(loadPlayerStories);
const PlayerStoryDetail = lazy(loadPlayerStoryDetail);
const MediaDiscoveryProgrammeJan26 = lazy(loadMediaDiscoveryProgrammeJan26);
const MediaDiscoveryDayVisualImpairment = lazy(loadMediaDiscoveryDayVisualImpairment);
const MediaAdaptiveGolfTournament = lazy(loadMediaAdaptiveGolfTournament);
const GetInvolved = lazy(loadGetInvolved);
const Donate = lazy(loadDonate);
const News = lazy(loadNews);
const NewsPostMilestone = lazy(loadNewsPostMilestone);
const NewsPostDiscoveryDay = lazy(loadNewsPostDiscoveryDay);
const NewsAdaptiveOpen = lazy(loadNewsAdaptiveOpen);
const NewsCzechDisableOpen = lazy(loadNewsCzechDisableOpen);
const Connect = lazy(loadConnect);
const Blog = lazy(loadBlog);
const BlogPostEmpowerment = lazy(loadBlogPostEmpowerment);
const BlogPostVolunteers = lazy(loadBlogPostVolunteers);
const BlogPostAdaptiveCoaching = lazy(loadBlogPostAdaptiveCoaching);
const BlogPostBeyondVision = lazy(loadBlogPostBeyondVision);
const BlogPostCzech = lazy(loadBlogPostCzech);
const BlogPostCol = lazy(loadBlogPostCol);
const BlogPostCol2 = lazy(loadBlogPostCol2);
const AboutUs = lazy(loadAboutUs);
const Team = lazy(loadTeam);

const DeferredAnalytics = lazy(() =>
  import('@vercel/analytics/react').then((module) => ({ default: module.Analytics }))
);
const DeferredSpeedInsights = lazy(() =>
  import('@vercel/speed-insights/react').then((module) => ({ default: module.SpeedInsights }))
);

function ScrollToTop() {
  const { pathname, key } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousScrollBehavior = html.style.scrollBehavior;

    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';

    html.scrollTop = 0;
    body.scrollTop = 0;
    window.scrollTo(0, 0);

    requestAnimationFrame(() => {
      html.scrollTop = 0;
      body.scrollTop = 0;
      window.scrollTo(0, 0);
    });

    return () => {
      html.style.scrollBehavior = previousScrollBehavior;
      body.style.scrollBehavior = '';
    };
  }, [pathname, key]);

  return null;
}

function App() {
  const [mountInsights, setMountInsights] = useState(false);

  useEffect(() => {
    const runInIdle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1200));
    const cancelIdle = window.cancelIdleCallback || window.clearTimeout;

    const idleId = runInIdle(() => {
      setMountInsights(true);
      Promise.allSettled([
        loadWhatIsAdaptiveGolf(),
        loadTryGolf(),
        loadGetInvolved(),
        loadDonate(),
        loadMedia(),
        loadPlayerStories(),
        loadAboutUs(),
      ]);
    });

    return () => cancelIdle(idleId);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-[40svh]" />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Homepage />} />
            <Route path="what-is-adaptive-golf" element={<WhatIsAdaptiveGolf />} />
            <Route path="try-golf" element={<TryGolf />} />
            <Route path="competition" element={<Competition />} />
            <Route path="competition/aga-calendar" element={<AgaCalendar />} />
            <Route path="media" element={<Media />} />
            <Route path="media/player-stories" element={<PlayerStories />} />
            <Route path="media/player-stories/:slug" element={<PlayerStoryDetail />} />
            <Route path="media/discovery-programme-26-jan-2026" element={<MediaDiscoveryProgrammeJan26 />} />
            <Route path="media/discovery-day-visual-impairment-participants" element={<MediaDiscoveryDayVisualImpairment />} />
            <Route path="media/adaptive-golf-tournament" element={<MediaAdaptiveGolfTournament />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/more-than-a-game" element={<BlogPostEmpowerment />} />
            <Route path="blog/power-of-volunteers" element={<BlogPostVolunteers />} />
            <Route path="blog/adaptive-coaching" element={<BlogPostAdaptiveCoaching />} />
            <Route path="blog/beyond-vision" element={<BlogPostBeyondVision />} />
            <Route path="blog/czech-disabled-golf-masters-2026" element={<BlogPostCzech />} />
            <Route path="blog/PostCol" element={<BlogPostCol />} />
            <Route path="blog/PostCol2" element={<BlogPostCol2 />} />
            <Route path="get-involved" element={<GetInvolved />} />
            <Route path="donate" element={<Donate />} />
            <Route path="news" element={<News />} />
            <Route path="news/a-milestone-moment" element={<NewsPostMilestone />} />
            <Route path="news/where-sound-leads-the-game" element={<NewsPostDiscoveryDay />} />
            <Route path="news/india-adaptive-open-2026" element={<NewsAdaptiveOpen />} />
            <Route path="news/czech-disabled-golf-masters-2026" element={<NewsCzechDisableOpen />} />
            <Route path="connect" element={<Connect />} />
            <Route path="about-us" element={<AboutUs />} />
            <Route path="team" element={<Team />} />
          </Route>
        </Routes>
      </Suspense>
      {mountInsights && (
        <Suspense fallback={null}>
          <DeferredAnalytics />
          <DeferredSpeedInsights />
        </Suspense>
      )}
    </Router>
  );
}

export default App;
